<script setup lang="ts">
// /privacy, /terms and /disclaimer — one view, three documents, picked by
// route meta (open-items review, item 11).
//
// One component because the three pages are structurally identical and
// three near-copies would drift. The content lives in content/legal.ts;
// this file is layout only.
//
// While LEGAL_STATUS is 'review', every page carries a banner saying so.
// Independent drafts are being commissioned to compare against these, and a
// policy that is quietly provisional is worse than one that admits it.
import { computed } from 'vue'
import { RouterLink, useRoute } from 'vue-router'

import { LEGAL_DOCS, type LegalDoc } from '../content/legal'
import { LEGAL_EFFECTIVE, LEGAL_LINKS, LEGAL_STATUS, SITE_EMAIL } from '../nav'

const route = useRoute()

const doc = computed<LegalDoc>(
    () => LEGAL_DOCS[(route.meta.doc as LegalDoc['id']) ?? 'disclaimer'],
)

/** The other two documents, for the cross-links at the foot of each. */
const siblings = computed(() =>
    LEGAL_LINKS.filter((l) => !l.href.endsWith(`/${doc.value.id}`)),
)
</script>

<template>
    <article class="legal">
        <header class="head">
            <div class="head-inner">
                <p class="crumb">Home <span>›</span> {{ doc.title }}</p>
                <p class="kicker">{{ doc.kicker }}</p>
                <h1>{{ doc.title }}</h1>
                <p class="lede">{{ doc.lede }}</p>
                <p class="dated">
                    <template v-if="LEGAL_STATUS === 'final'">
                        Effective {{ LEGAL_EFFECTIVE }}
                    </template>
                    <template v-else>Draft of {{ LEGAL_EFFECTIVE }}</template>
                </p>
            </div>
        </header>

        <div v-if="LEGAL_STATUS === 'review'" class="review">
            <div class="review-inner">
                <span class="mark" aria-hidden="true">!</span>
                <div>
                    <strong>This document is in review and is not yet final.</strong>
                    <p>
                        It is being compared against independently prepared versions before it is
                        adopted. Read it as a statement of intent rather than a finished policy, and
                        send anything that looks wrong to
                        <a :href="`mailto:${SITE_EMAIL}`">{{ SITE_EMAIL }}</a
                        >.
                    </p>
                </div>
            </div>
        </div>

        <div class="body">
            <section v-for="(c, i) in doc.clauses" :key="i" class="clause">
                <h2 v-if="c.h">{{ c.h }}</h2>
                <p v-if="c.p">{{ c.p }}</p>
                <ul v-if="c.list">
                    <li v-for="item in c.list" :key="item">{{ item }}</li>
                </ul>
            </section>

            <nav class="siblings">
                <RouterLink v-for="s in siblings" :key="s.href" :to="s.href">
                    {{ s.label }} →
                </RouterLink>
            </nav>
        </div>
    </article>
</template>

<style scoped lang="scss">
.head {
    background: var(--site-gradient-navy);
    color: #fff;
}

.head-inner {
    max-width: 52rem;
    margin: 0 auto;
    padding: var(--site-space-xl) var(--site-space-lg);
}

.crumb {
    margin: 0 0 var(--site-space-sm);
    font-size: 0.76rem;
    color: rgba(255, 255, 255, 0.62);

    span {
        margin: 0 0.3rem;
    }
}

.kicker {
    margin: 0 0 0.3rem;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--site-accent);
}

h1 {
    font-family: var(--site-font-heading);
    font-size: clamp(1.6rem, 3.2vw, 2.3rem);
    font-weight: 800;
    margin: 0 0 var(--site-space-sm);
}

.lede {
    margin: 0;
    max-width: 40rem;
    color: rgba(255, 255, 255, 0.86);
    line-height: 1.6;
}

.dated {
    margin: var(--site-space-md) 0 0;
    font-size: 0.78rem;
    color: rgba(255, 255, 255, 0.6);
}

/* ---- review banner ---- */
.review {
    background: var(--site-accent-soft);
    border-bottom: 1px solid var(--site-border);
}

.review-inner {
    max-width: 52rem;
    margin: 0 auto;
    padding: var(--site-space-lg);
    display: flex;
    gap: 0.8rem;
    align-items: flex-start;

    strong {
        display: block;
        font-family: var(--site-font-heading);
        font-size: 0.92rem;
        color: var(--site-navy);
        margin-bottom: 0.2rem;
    }

    p {
        margin: 0;
        font-size: 0.85rem;
        line-height: 1.6;
        color: var(--site-text-muted);
    }

    a {
        color: var(--site-blue);
        font-weight: 600;
    }
}

.mark {
    display: grid;
    place-items: center;
    width: 1.6rem;
    height: 1.6rem;
    flex-shrink: 0;
    border-radius: 50%;
    background: var(--site-accent);
    color: #fff;
    font-family: var(--site-font-heading);
    font-weight: 800;
    font-size: 0.9rem;
}

/* ---- document body ---- */
.body {
    max-width: 52rem;
    margin: 0 auto;
    padding: var(--site-section-pad) var(--site-space-lg);
}

.clause {
    margin-bottom: var(--site-space-lg);

    &:last-of-type {
        margin-bottom: 0;
    }
}

h2 {
    font-family: var(--site-font-heading);
    font-size: 1.06rem;
    font-weight: 700;
    color: var(--site-navy);
    margin: 0 0 0.45rem;
}

p {
    margin: 0;
    font-size: 0.92rem;
    line-height: 1.72;
    color: var(--site-text);
}

ul {
    margin: 0.6rem 0 0;
    padding-left: 1.15rem;
    display: grid;
    gap: 0.45rem;

    li {
        font-size: 0.9rem;
        line-height: 1.68;
        color: var(--site-text-muted);

        &::marker {
            color: var(--site-accent);
        }
    }
}

.siblings {
    display: flex;
    flex-wrap: wrap;
    gap: var(--site-space-md);
    margin-top: var(--site-space-xl);
    padding-top: var(--site-space-lg);
    border-top: 1px solid var(--site-border);

    a {
        display: inline-flex;
        align-items: center;
        font-size: 0.88rem;
        font-weight: 600;
        color: var(--site-blue);
        text-decoration: none;

        &:hover {
            color: var(--site-navy);
        }

        @media (max-width: 860px) {
            min-height: 44px;
        }
    }
}
</style>
